<template>
  <!-- BEGIN: Top Bar -->
  <div class="top-bar-themed bg-primary-1 text-white -mt-7 md:-mt-5 -mx-3 sm:-mx-8">
    <div class="flex items-center h-16 px-3 sm:px-6">
      <!-- BEGIN: Logo / Brand -->
      <router-link
        :to="{ name: 'main-house-hold' }"
        class="flex items-center shrink-0"
      >
        <div class="bg-white rounded-md px-3 py-1.5 flex items-center justify-center">
          <img
            src="@/assets/images/main/Logo-Local-census.png"
            alt="TPMAP"
            class="h-6 object-contain"
          >
        </div>
        <span class="hidden md:inline text-white text-base font-medium ml-3 text-logo">
          Local Census
        </span>
      </router-link>
      <!-- END: Logo / Brand -->

      <!-- BEGIN: Horizontal Menu -->
      <nav
        v-if="!isHideMenu"
        class="hidden md:flex items-center gap-1 lg:gap-2 mx-auto"
      >
        <template v-for="(menu, menuKey) in topMenu" :key="menu.title + menuKey">
          <!-- single item -->
          <a
            v-if="!menu.subMenu"
            :href="resolveHref(menu)"
            class="top-menu-item"
            :class="{ 'top-menu-item--active': menu.active }"
            @click="onMenuClick(menu, $event)"
          >
            {{ menu.title }}
          </a>
          <!-- dropdown item -->
          <div
            v-else
            class="dropdown"
          >
            <a
              role="button"
              aria-expanded="false"
              class="top-menu-item dropdown-toggle cursor-pointer flex items-center"
              :class="{ 'top-menu-item--active': menu.active }"
            >
              {{ menu.title }}
              <ChevronDownIcon class="w-4 h-4 ml-1" />
            </a>
            <div class="dropdown-menu w-56">
              <div class="dropdown-menu__content box bg-primary-1 text-white">
                <template
                  v-for="(sub, subKey) in menu.subMenu"
                  :key="sub.title + subKey"
                >
                  <a
                    :href="resolveHref(sub)"
                    class="flex items-center p-2 transition duration-300 ease-in-out hover:bg-white/10 rounded-md"
                    :class="{ 'bg-white/10': sub.active }"
                    @click="onMenuClick(sub, $event)"
                    data-dismiss="dropdown"
                  >
                    {{ sub.title }}
                  </a>
                </template>
              </div>
            </div>
          </div>
        </template>
      </nav>
      <!-- END: Horizontal Menu -->

      <!-- BEGIN: Breadcrumb (when menu hidden) -->
      <Breadcrumb
        v-if="isHideMenu"
        :is-hide-menu="isHideMenu"
        :bread-crumb-list="breadCrumbList"
        class="mx-4"
      />
      <!-- END: Breadcrumb -->

      <!-- BEGIN: User block -->
      <div class="flex items-center ml-auto md:ml-4 gap-2 sm:gap-3">
        <template v-if="isUserAuth">
          <div class="hidden sm:block text-right leading-tight">
            <div class="text-sm">
              {{ userEmail }}
            </div>
            <div class="text-xs text-theme-13">
              {{ userRoleLabel }}
            </div>
          </div>
          <button
            type="button"
            class="w-8 h-8 rounded-md bg-white text-primary-1 hover:bg-white/20 hover:text-white flex items-center justify-center"
            :title="onlineComputed ? 'ตั้งค่า' : 'ออฟไลน์'"
          >
            <SettingsIcon
              v-if="onlineComputed"
              class="w-4 h-4"
            />
            <WifiOffIcon
              v-else
              class="w-4 h-4"
            />
          </button>
          <button
            type="button"
            class="w-8 h-8 rounded-md bg-white text-primary-1 hover:bg-white/20 hover:text-white flex items-center justify-center"
            title="ออกจากระบบ"
            @click="logoutUser"
          >
            <LogOutIcon class="w-4 h-4" />
          </button>
        </template>
        <a
          v-else
          href="#"
          class="text-white text-sm"
          @click.prevent="createLoginUrl"
        >
          เข้าสู่ระบบ
        </a>
      </div>
      <!-- END: User block -->
    </div>
  </div>
  <!-- END: Top Bar -->
</template>

<script>
import { defineComponent, computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useStore } from '@/store'
import { logoutUser } from '@/utils/auth'
import { helper as $h } from '@/utils/helper'
import { nestedMenu, linkTo } from '@/layouts/side-menu/index'
import Breadcrumb from '@/components/breadcrumb/index.vue'

export default defineComponent({
  components: {
    Breadcrumb
  },
  props: {
    isHideMenu: {
      type: Boolean,
      default: false
    },
    breadCrumbList: {
      type: Array,
      default: () => []
    }
  },
  setup() {
    const route = useRoute()
    const router = useRouter()
    const store = useStore()

    const topMenu = computed(() => {
      const menuList = store.getters['sideMenu/menu'].filter(m => m.key !== 'devider')
      return nestedMenu($h.toRaw(menuList), route)
    })

    const resolveHref = (menu) => {
      if (menu.subMenu) return 'javascript:;'
      if (!menu.pageName) return '#'
      const opts = { name: menu.pageName }
      if (menu.params) opts.params = { ...menu.params }
      return router.resolve(opts).path
    }

    const onMenuClick = (menu, event) => {
      linkTo(menu, router, event)
    }

    const isUserAuth = computed(() => !!store.getters['userAuth/getUserAuth'])
    const tokenParsed = computed(() => store.getters['userAuth/getTokenParsed'] || {})
    const userEmail = computed(() => {
      return tokenParsed.value.email
        || tokenParsed.value.preferred_username
        || store.getters['userAuth/getUsername']
    })
    const roles = computed(() => store.getters['userAuth/getRole'] || [])
    const userRoleLabel = computed(() => {
      const r = roles.value
      if (!r || !r.length) return store.getters['userAuth/getUserPositionNameShort'] || ''
      if (r.includes('super_user') || r.includes('superuser') || r.includes('super-admin')) return 'Super user'
      if (r.includes('admin')) return 'Admin'
      return r[0]
    })
    const onlineComputed = computed(() => store.getters['network/isOnline'])

    const createLoginUrl = () => {
      localStorage.setItem('mso_kc_fauth', true)
      window.location.href = store.getters['userAuth/getAllKeycloak'].createLoginUrl()
    }
    return {
      topMenu,
      router,
      resolveHref,
      onMenuClick,
      isUserAuth,
      userEmail,
      userRoleLabel,
      onlineComputed,
      createLoginUrl,
      logoutUser
    }
  }
})
</script>

<style lang="scss" scoped>
.top-bar-themed {
  position: relative;
  z-index: 0;
}
.top-menu-item {
  color: #ffffff;
  padding: 0.4rem 0.85rem;
  border-radius: 0.375rem;
  font-size: 0.9rem;
  white-space: nowrap;
  transition: background-color 0.2s ease;
  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
  }
  &--active {
    background-color: rgba(255, 255, 255, 0.18);
    font-weight: 500;
  }
}

.text-logo {
  border-left: 1px solid #5B7B9C;
  padding: 6px;
  padding-left: 10px;
}
</style>
